"use client";

import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { themes } from "@/constants/widget";
import { useWorkspace } from "@/api/auth/queries";
import { useUpdateWidgetCustomisation } from "@/api/workspace/queries";
import { LoadingSpinner } from "@/components/ui/spinner";
import LimitBlocker from "@/components/billing/LimitBlocker";

const Customisation = () => {
  const workspace = useWorkspace();
  const { mutateAsync: updateWidgetCustomisation } =
    useUpdateWidgetCustomisation();

  const [isDarkMode, setIsDarkMode] = useState<boolean | undefined>(undefined);
  const [presetQuestions, setPresetQuestions] = useState<string[]>([]);
  const [newQuestion, setNewQuestion] = useState("");
  const [selectedTheme, setSelectedTheme] = useState(themes[0]);

  const addPresetQuestion = () => {
    if (newQuestion && presetQuestions.length < 3) {
      setPresetQuestions([...presetQuestions, newQuestion]);
      setNewQuestion("");
    }
  };

  const removePresetQuestion = (index: number) => {
    setPresetQuestions(presetQuestions.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (workspace) {
      setIsDarkMode(workspace.widget_customisation.dark_mode);
      setPresetQuestions(workspace.widget_customisation.preset_questions);
      setSelectedTheme(
        themes.find(
          (theme) => theme.key === workspace.widget_customisation.theme_option
        ) || themes[0]
      );
    }
  }, [workspace]);

  const handleSaveChanges = async () => {
    await updateWidgetCustomisation({
      theme_option: selectedTheme.key,
      preset_questions: presetQuestions,
      dark_mode: isDarkMode || false,
    });
  };

  if (isDarkMode === undefined)
    return (
      <div className="w-full h-full py-72 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );

  return (
    <LimitBlocker limit="custom_branding">
      <div className="w-full flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <Label htmlFor="dark-mode" className="text-base">
            Dark Mode
          </Label>
          <Switch
            id="dark-mode"
            checked={isDarkMode}
            onCheckedChange={setIsDarkMode}
          />
        </div>
        <Separator />
        <div className="flex flex-col gap-4">
          <Label htmlFor="preset-questions" className="text-base">
            Preset Questions
          </Label>
          <ul className="space-y-2">
            {presetQuestions.map((question, index) => (
              <li
                key={index}
                className="flex items-center justify-between p-2 bg-secondary rounded-md">
                <span className="px-2">{question}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removePresetQuestion(index)}>
                  <Trash2 className="h-4 w-4" />
                  <span className="sr-only">Remove question</span>
                </Button>
              </li>
            ))}
          </ul>
          {presetQuestions.length < 3 && (
            <div className="flex space-x-2">
              <Input
                id="preset-questions"
                placeholder="Enter a preset question"
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
              />
              <Button onClick={addPresetQuestion}>Add</Button>
            </div>
          )}
          {presetQuestions.length >= 3 && (
            <p className="text-sm text-muted-foreground">
              You've reached the maximum of 3 preset questions.
            </p>
          )}
        </div>
        <Separator />
        <div className="flex flex-col gap-4">
          <Label className="text-base">Theme</Label>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {themes.map((theme) => (
              <Card
                key={theme.key}
                className={`cursor-pointer transition-all ${
                  selectedTheme.key === theme.key ? "ring-2 ring-primary" : ""
                }`}
                onClick={() => setSelectedTheme(theme)}>
                <CardContent className="p-4 flex flex-col items-center">
                  <div className="flex space-x-2 mb-2">
                    <div
                      className={`w-6 h-6 rounded-full ${theme.primary}`}></div>
                    <div
                      className={`w-6 h-6 rounded-full ${theme.secondary}`}></div>
                  </div>
                  <span className="text-sm font-medium">{theme.label}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
        <Button className="w-full mt-6" onClick={handleSaveChanges}>
          Save Changes
        </Button>
      </div>
    </LimitBlocker>
  );
};

export default Customisation;
